/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { BOOKING_CALENDAR_CELL_BEGIN_END } from '../../../../brands/default/content/components/deal';
import { trackEvent } from '../../../../helpers/analytics';
import ThemeContext from '../../../../providers/ThemeProvider';

const BookingCalendarCellBeginEnd = ({
  cell,
  index,
  onHoverDate,
  onSelect,
  onMonthChange,
}) => {
  const theme = useContext(ThemeContext);
  const isVipUser = useSelector((state) => state.user.userprofile.vipUser);
  const { soldOut } = BOOKING_CALENDAR_CELL_BEGIN_END;
  const date = cell.key;

  const finalPrice =
    cell.isMarketingCreditDeal && isVipUser ? cell.vipPrice : cell.minPrice;

  const handleOnClick = (cellIndex) => {
    if (!cell?.isSelectable) return;
    if (!cell.inMonth) onMonthChange(cell.key);
    onSelect({ cellIndex, date });
    trackEvent('travel_date_selected');
  };

  const handleOnMouseEnter = (hoverDate) => {
    onHoverDate(hoverDate);
  };

  return (
    <>
      <div
        aria-label={cell?.isSelectable ? `Select ${cell.key}` : undefined}
        className={classNames('booking-calendar__cell', {
          'booking-calendar__cell--fade': !cell.inMonth,
          'booking-calendar__cell--hover': cell.isHovered,
          'booking-calendar__cell--no-selectable': !cell?.isSelectable,
          'booking-calendar__cell--selectable': cell?.isSelectable,
          'booking-calendar__cell--selected': cell.isSelected,
          'booking-calendar__cell--selected-first': cell.isFirstDateInSelection,
          'booking-calendar__cell--selected-last': cell.isLastDateInSelection,
          'booking-calendar__cell--sold-out': cell.soldOut,
        })}
        onClick={() => handleOnClick(index)}
        onFocus={() => handleOnMouseEnter(cell?.isSelectable && cell.date)}
        onKeyDown={(event) => event.key === 'Enter' && handleOnClick()}
        onMouseEnter={() => handleOnMouseEnter(cell?.isSelectable && cell.date)}
        role={cell?.isSelectable ? 'button' : undefined}
        tabIndex={cell?.isSelectable ? 0 : -1}
      >
        {!cell.isSelected && (
          <>
            <p className="booking-calendar__cell-label">{cell.label}</p>
            {cell.minPrice && !cell.soldOut && (
              <p className="booking-calendar__price">{finalPrice}</p>
            )}
            {cell.soldOut && (
              <p className="booking-calendar__sold-out">{soldOut}</p>
            )}
          </>
        )}

        {cell.isSelected && (
          <>
            <p className="booking-calendar__cell-label">{cell.label}</p>
          </>
        )}
      </div>
      <style jsx>{`
        .booking-calendar__cell {
          box-sizing: border-box;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          justify-content: center;
          height: 78px;
          // border-bottom: 1px solid #cccccc;
          border-right: 1px solid #cccccc;
          border-top: 1px solid #cccccc;
          width: calc(100% / 7);
          flex-grow: 1;
          outline-offset: -2px;
        }

        .booking-calendar__cell--hover {
          background-color: #1a1a1a20;
        }

        .booking-calendar__cell--selected {
          background-color: #1a1a1a20;
          border-right: none;
          color: ${theme.colors.textonprimary};
        }

        .booking-calendar__cell--selected-first {
          background-color: ${theme.colors.primary};
          border-left: 2px solid ${theme.colors.primary};
          border-radius: 4px 0 0 4px;
        }

        .booking-calendar__cell--selected-last {
          background-color: ${theme.colors.primary};
          border-right: 2px solid ${theme.colors.primary} !important;
          border-radius: 0 4px 4px 0;
        }

        .booking-calendar__cell--selectable:focus {
          outline: 2px solid ${theme.colors.primary};
        }

        .booking-calendar__cell--selectable:hover {
          outline: 2px solid ${theme.colors.primary};
        }

        .booking-calendar__cell--selected-last:focus {
          outline: none;
        }

        /* right column */
        .booking-calendar__cell:nth-child(7n + 7) {
          border-right: none;
        }

        /* bottom row */
        .booking-calendar__cell:nth-last-child(-n + 7) {
          border-bottom: none;
        }

        .booking-calendar__cell-label {
          position: absolute;
          top: 4px;
          left: 4px;
          font-size: 12px;
          font-weight: bold;
          line-height: 1em;
          color: #666;
        }

        .booking-calendar__cell--no-selectable .booking-calendar__cell-label,
        .booking-calendar__cell--no-selectable .booking-calendar__price,
        .booking-calendar__cell--fade .booking-calendar__cell-label {
          color: #000000;
        }

        .booking-calendar__cell--no-selectable p,
        .booking-calendar__cell--fade p,
        .booking-calendar__cell--sold-out p {
          opacity: 0.2;
        }

        .booking-calendar__price {
          font-size: 16px;
          font-weight: bold;
          line-height: 20px;
          margin: 0;
        }

        .booking-calendar__cell--selected-first .booking-calendar__price,
        .booking-calendar__cell--selected-last .booking-calendar__price,
        .booking-calendar__cell--selected-first .booking-calendar__cell-label,
        .booking-calendar__cell--selected-last .booking-calendar__cell-label,
        .booking-calendar__cell--selected-last .booking-calendar__cell-label {
          color: white;
          opacity: 1;
        }

        .booking-calendar__cell--selected-last .booking-calendar__price {
          display: none;
        }

        .booking-calendar__sold-out {
          font-size: 16px;
          font-weight: bold;
          margin: 0;
          color: ${theme.colors.primary};
          line-height: 1.1em;
        }
      `}</style>
    </>
  );
};

BookingCalendarCellBeginEnd.propTypes = {
  cell: PropTypes.object,
  index: PropTypes.number,
  onHoverDate: PropTypes.func,
  onMonthChange: PropTypes.func,
  onSelect: PropTypes.func,
};

BookingCalendarCellBeginEnd.defaultProps = {
  cell: {},
  index: 0,
  onHoverDate: () => false,
  onMonthChange: () => false,
  onSelect: () => false,
};

export default BookingCalendarCellBeginEnd;
